import { InfiniteData, QueryKey, UseInfiniteQueryOptions, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { deserialize } from 'serializr';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { QueryKeys } from '../../enums/queryKeys';
import { UsersListResponse } from '../../models/userManager/userRecord.model';
import { UserDetails } from '../../models/userManager/userDetails.model';
import type {
  CreateUserPayload,
  OrgGroup,
  RolesAvailabilityResponse,
  UpdateUserPayload,
  UsersPage,
  UserSortField,
  UserSortOrder,
} from '../../types/UserManagementTypes';

// Fixed 64px row height (CustomTable.module.scss) + header comfortably fits ~20 rows on
// common desktop viewports without virtualisation, so one fetch fills the view.
const USERS_PER_PAGE = 20;

// This API's error body uses `error`, not `message` (unlike this repo's other services).
const getUserManagerErrorMessage = (error: unknown): string | undefined => {
  const axiosError = error as AxiosError<{ error?: string }>;
  return axiosError?.response?.data?.error || axiosError?.message;
};

const getUserManagerErrorCode = (error: unknown): string | undefined => {
  const axiosError = error as AxiosError<{ errorCode?: string }>;
  return axiosError?.response?.data?.errorCode;
};

const deriveDurationStatus = (accessExpiresAt: string | null): 'normal' | 'critical' | undefined => {
  if (!accessExpiresAt) return undefined;
  const CRITICAL_WINDOW_DAYS = 30;
  const daysRemaining = (new Date(accessExpiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000);
  return daysRemaining <= CRITICAL_WINDOW_DAYS ? 'critical' : 'normal';
};

const buildUserPayload = (userId: number, payload: UpdateUserPayload) => ({ id: userId, payload });

const UserManagerService = () => {
  const getUsersInfinite = (
    sortBy: UserSortField,
    sortOrder: UserSortOrder,
  ): UseInfiniteQueryOptions<UsersPage, Error, InfiniteData<UsersPage>, QueryKey, number> => ({
    queryKey: [QueryKeys.GET_USERS_LIST, sortBy, sortOrder],
    queryFn: async ({ pageParam }) => {
      const { data } = await axiosInstance.get(ApiRoutes.USERS, {
        params: { sort_by: sortBy, sort_order: sortOrder === 'ascend' ? 'asc' : 'desc', limit: USERS_PER_PAGE, offset: pageParam },
      });
      const { users, pagination } = deserialize(UsersListResponse, data);
      users.forEach((user) => {
        user.durationStatus = deriveDurationStatus(user.accessExpiresAt);
      });
      return { users, nextOffset: pagination.hasMore ? pagination.offset + pagination.limit : null };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
  });

  const getUserDetails = (userId: number): UseQueryOptions<UserDetails, Error> => ({
    queryKey: [QueryKeys.GET_USER_DETAILS, userId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(ApiRoutes.USER_BY_ID.replace(':id', String(userId)));
      return deserialize(UserDetails, data.user);
    },
  });

  const updateUser = (): UseMutationOptions<{ message: string; userId: number }, Error, ReturnType<typeof buildUserPayload>> => ({
    mutationFn: async ({ id, payload }) => {
      const { data } = await axiosInstance.put(ApiRoutes.USER_BY_ID.replace(':id', String(id)), payload);
      return data;
    },
  });

  const deleteUser = (): UseMutationOptions<{ message: string; userId: number; deletedAt: string }, Error, number> => ({
    mutationFn: async (userId) => {
      const { data } = await axiosInstance.delete(ApiRoutes.USER_BY_ID.replace(':id', String(userId)));
      return data;
    },
  });

  const createUser = (): UseMutationOptions<
    { userId: number; name: string; email: string; roleId: number; message: string },
    Error,
    CreateUserPayload
  > => ({
    mutationFn: async (payload) => {
      const { data } = await axiosInstance.post(ApiRoutes.USERS, payload);
      return data;
    },
  });

  // Proactive UX only -- backend still rejects an over-quota role at submit either way.
  const getRolesAvailability = (): UseQueryOptions<RolesAvailabilityResponse, Error> => ({
    queryKey: [QueryKeys.GET_ROLES_AVAILABILITY],
    queryFn: async () => {
      const { data } = await axiosInstance.get(ApiRoutes.ROLES_AVAILABILITY);
      return data;
    },
  });

  const getGroupsList = (): UseQueryOptions<OrgGroup[], Error> => ({
    queryKey: [QueryKeys.GET_ORG_GROUPS],
    queryFn: async () => {
      const { data } = await axiosInstance.get(ApiRoutes.GROUPS);
      return data.groups;
    },
  });

  const createGroup = (): UseMutationOptions<{ id: number; name: string; message: string }, Error, { name: string }> => ({
    mutationFn: async ({ name }) => {
      const { data } = await axiosInstance.post(ApiRoutes.GROUPS, { name });
      return data;
    },
  });

  return { getUsersInfinite, getUserDetails, updateUser, deleteUser, createUser, getRolesAvailability, getGroupsList, createGroup };
};

export default UserManagerService;
export { getUserManagerErrorMessage, getUserManagerErrorCode, buildUserPayload };
